import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, ResponsiveContainer, Tooltip } from 'recharts';
import { Euro, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ymCurrent = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const ymMinus = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const fmtMonth = (ym) => {
  const names = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Déc'];
  return names[parseInt(ym.split('-')[1]) - 1];
};

const fmtEur = (v) => (v ?? 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
});

export default function ROIKpiCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(
          `${API}/settings/leads/roi?start=${ymMinus(5)}&end=${ymCurrent()}`,
          { withCredentials: true }
        );
        setData(d);
      } catch {
        // Silent — users without permission or missing data
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) return null;

  // Build monthly aggregates across all clients
  const monthly = data.month_labels.map(ym => {
    let rev = 0, cost = 0;
    data.rows.forEach(r => {
      const mData = r.monthly.find(m => m.month === ym);
      rev += (mData?.count || 0) * (r.lead_value || 0);
      cost += r.monthly_marketing_cost || 0;
    });
    return {
      month: fmtMonth(ym),
      revenu: Math.round(rev),
      net: Math.round(rev - cost),
    };
  });

  // Current month (last in array) values
  const current = monthly[monthly.length - 1] || { revenu: 0, net: 0 };
  const prev = monthly[monthly.length - 2] || { net: 0 };
  const delta = current.net - prev.net;
  const deltaPct = prev.net !== 0 ? Math.round((delta / Math.abs(prev.net)) * 100) : null;

  const noConfig = data.rows.every(r => !r.lead_value && !r.monthly_marketing_cost);

  return (
    <div className="bg-white border border-neutral-200 p-6" data-testid="dashboard-roi-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#002FA7]/10 rounded flex items-center justify-center">
            <Euro className="w-5 h-5 text-[#002FA7]" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-neutral-900">ROI Marketing</h2>
            <p className="text-xs text-neutral-500">
              {noConfig ? 'Configurez la valeur/coût par client' : '6 derniers mois'}
            </p>
          </div>
        </div>
        <Link to="/lead-analytics" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1"
              data-testid="roi-see-more">
          Détails <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      {noConfig ? (
        <div className="py-6 text-center text-sm text-neutral-400">
          Aucune rentabilité calculée.<br />
          <Link to="/lead-analytics" className="text-[#002FA7] hover:underline text-xs">
            Définir les valeurs
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">Net mois en cours</p>
            <p className={`font-mono text-3xl font-bold mt-2 ${current.net >= 0 ? 'text-[#002FA7]' : 'text-red-600'}`}>
              {fmtEur(current.net)}
            </p>
            {deltaPct !== null && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${delta >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {delta >= 0 ? '+' : ''}{deltaPct}% vs mois précédent
              </p>
            )}
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-neutral-500">Tendance net</p>
            <div className="h-16 mt-1">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <Tooltip
                    cursor={{ fill: 'rgba(0,47,167,0.05)' }}
                    contentStyle={{
                      backgroundColor: '#0A0A0A', border: 'none', borderRadius: '4px',
                      color: '#fff', fontSize: '11px',
                    }}
                    formatter={v => [fmtEur(v), 'Net']}
                  />
                  <Bar dataKey="net" radius={[2, 2, 0, 0]}>
                    {monthly.map((m) => (
                      <rect key={m.month} fill={m.net >= 0 ? '#002FA7' : '#E76F51'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-between text-[10px] font-mono text-neutral-400 mt-1">
              {monthly.map(m => <span key={m.month}>{m.month}</span>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
